<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">
      Сведения о доверенности в машиночитаемом виде
    </div>

    <v-container>
      <div class="form-base">
        <MrpaUploader
          :v="$v"
          @parsed-mrpa-change="parsedMrpaChangeHandler"
          :statement-id="$route.params.statementId"
          :statement-type="statementType"
        />

        <template v-if="formVisible">
          <div class="form-section-base__controls">
            <v-row>
              <v-col>
                <v-radio-group v-model="role" row disabled>
                  <v-radio label="Физическое лицо" :value="Role.PERSON" />
                  <v-radio
                    label="Юридическое лицо"
                    :value="Role.ORGANIZATION"
                  />
                  <v-radio
                    label="Иностранное юридическое лицо"
                    :value="Role.FOREIGN_ORGANIZATION"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </div>

          <Person v-if="role === Role.PERSON" :v="$v.subject.person" />

          <Organization
            v-if="role === Role.ORGANIZATION"
            :v="$v.subject.organization"
          />

          <ForeignOrganization
            v-if="role === Role.FOREIGN_ORGANIZATION"
            :v="$v.subject.foreignOrganization"
          />
        </template>

        <Footer
          @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
          @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
          :loading-next="loadingNext"
        />
      </div>
    </v-container>
  </section>
</template>

<script>
import { mdiAlertCircleOutline } from '@mdi/js';
import { Role } from '@/utils/dictionaries';
import { leaveStep, checkInvalid } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import { getValidationErrorText } from '@/utils/validate';
import cloneDeep from 'clone-deep';
import { ServicesModule } from '@/store/modules/services';
import deepmerge from 'deepmerge';
import { MrpaType } from '@/constants';
import MrpaUploader, {
  mrpaUploaderModel,
  mrpaUploaderValidation,
  getMrpa,
  prefillMrpDisabled,
} from '@/components/services/elements/MrpaUploader';
import Person, { personModel, getPersonValidation } from './Person';
import Organization, {
  organizationModel,
  getOrganizationValidation,
} from './Organization';
import ForeignOrganization, {
  foreignOrganizationModel,
  getForeignOrganizationValidation,
} from './ForeignOrganization';
import { MrpaSubjectModule } from '@/store/modules/mrpaSubject';
import {
  assemblyMrpaTrustee,
  getDefaultMachineTrustee,
} from '@/components/services/elements/MrpaTrustee';

const getDefaultForm = () => ({
  person: cloneDeep(personModel),
  organization: cloneDeep(organizationModel),
  foreignOrganization: cloneDeep(foreignOrganizationModel),
});

export const assemblyMrpaModel = ({ subject, role, mrpa }) => {
  const subjectEntity = cloneDeep(subject);
  const trustee = subjectEntity[role].trustee;

  delete subjectEntity[role].trustee;

  return {
    [role]: subjectEntity[role],
    mrpType: mrpa.type,
    trustee: assemblyMrpaTrustee({
      trustee,
      mrpa,
    }),
  };
};

// если появятся различия в полях, делать отдельную форму для конкретного НД
export default {
  name: 'MrpaSubjectForm',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    subjectName: {
      type: String,
      default: 'applicant',
    },
    statementType: {
      type: String,
    },
  },
  components: {
    Person,
    Organization,
    ForeignOrganization,
    Footer,
    MrpaUploader,
  },
  data() {
    return {
      mdiAlertCircleOutline,
      getValidationErrorText,
      mrpa: cloneDeep(mrpaUploaderModel.mrpa),
      formVisible: mrpaUploaderModel.formVisible,
      subject: getDefaultForm(),
      StepDirection,
      StepName,
      leaveStep,
      Role,
      invalidFields: [],
      role: Role.FOREIGN_ORGANIZATION,
      trusteeRole: Role.PERSON,
    };
  },
  validations() {
    return {
      ...mrpaUploaderValidation,
      subject: {
        person: getPersonValidation(this.subject.person),
        organization: getOrganizationValidation(this.subject.organization),
        foreignOrganization: getForeignOrganizationValidation(
          this.subject.foreignOrganization
        ),
      },
    };
  },
  created() {
    const subjectStore = cloneDeep(
      ServicesModule.statement.subjects[this.subjectName]
    );

    if (subjectStore?.trustee) {
      this.prefillDraft(subjectStore, false);
    }
  },
  methods: {
    prefillDraft(subject, isMrpa) {
      if (isMrpa) {
        prefillMrpDisabled(subject);
      }

      if (subject.person) {
        this.role = Role.PERSON;
        this.subject.person = deepmerge.all([
          personModel,
          subject.person,
          {
            trustee: getDefaultMachineTrustee(subject.trustee),
          },
        ]);
      }

      if (subject.organization) {
        this.role = Role.ORGANIZATION;
        this.subject.organization = deepmerge.all([
          organizationModel,
          subject.organization,
          {
            trustee: getDefaultMachineTrustee(subject.trustee),
          },
        ]);
      }

      if (subject.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.subject.foreignOrganization = deepmerge.all([
          foreignOrganizationModel,
          subject.foreignOrganization,
          {
            trustee: getDefaultMachineTrustee(subject.trustee),
          },
        ]);
      }

      this.mrpa = getMrpa(subject);
      this.formVisible = true;
    },

    assemblyModel() {
      return assemblyMrpaModel({
        subject: this.subject,
        role: this.role,
        mrpa: this.mrpa,
      });
    },

    async touchFields() {
      this.invalidFields = [];

      this.$v.formVisible.$touch();

      if (this.$v.formVisible.$invalid) {
        checkInvalid(this.$v.formVisible, this.invalidFields);
        return;
      }

      this.$v.subject[this.role].$touch();

      checkInvalid(this.$v.subject[this.role], this.invalidFields);

      if (this.invalidFields.length === 0) {
        if (this.mrpa.type === MrpaType.BY_GUID) {
          const isMrpaValid = await this.checkMrpaGuid();

          if (!isMrpaValid) this.invalidFields.push(true);
        }
      }
    },

    parsedMrpaChangeHandler(parsedMrpa) {
      if (parsedMrpa) {
        this.prefillDraft(parsedMrpa, true);
        this.formVisible = true;
      } else {
        this.role = Role.PERSON;
        this.subject = getDefaultForm();
        this.formVisible = null;
      }
    },

    async checkMrpaGuid() {
      try {
        await MrpaSubjectModule.checkMrpaSubjectGuid({
          statementId: this.$route.params.statementId,
          guid: this.mrpa.guid,
          issuerInn: this.mrpa.issuerInn,
        });

        return true;
      } catch (e) {
        return false;
      }
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: StepName.S2,
      });
    },
  },
};
</script>
